import { useState } from "react"
import { formatDistanceToNowStrict } from "date-fns"
import { IconArrowRotateClockwiseOutline18 } from "nucleo-ui-outline-18"

import type {
	CampaignFunnel,
	Lane,
	LaneFunnel,
} from "@productlane/api/src/lib/reporting/lemlist-campaigns"
import { cn } from "@productlane/lib/src/browser"
import {
	Badge,
	IconButton,
	Tabs,
	TabsList,
	TabsTrigger,
	Tooltip,
} from "@productlane/ui"

import { ReportingChartFrame, toneColor } from "@/components/reporting/charts"
import { api } from "@/lib/api"
import {
	CAMPAIGN_COPY,
	campaignShortName,
	laneFootnote,
	percent,
	stepRows,
	waitingTotal,
} from "./gtm-campaigns-view"

type Status = "running" | "all"

const LANE_LABEL: Record<Lane, string> = {
	linkedin: "LinkedIn lane",
	email: "Email lane",
}

const borderRow =
	"border-b border-gray-200 last:border-0 dark:border-gray-700/80"

export function GtmCampaigns() {
	const [status, setStatus] = useState<Status>("running")
	const [selected, setSelected] = useState("all")
	const [isRefreshing, setIsRefreshing] = useState(false)
	const utils = api.useUtils()
	const query = api.analytics.lemlistCampaigns.useQuery(
		{ status },
		{ keepPreviousData: true },
	)
	const data = query.data

	const refresh = async () => {
		setIsRefreshing(true)
		try {
			await utils.analytics.lemlistCampaigns.fetch({ status, refresh: true })
			await utils.analytics.lemlistCampaigns.invalidate()
		} finally {
			setIsRefreshing(false)
		}
	}

	if (data && !data.configured) {
		return (
			<ReportingChartFrame
				title="Campaigns"
				isEmpty
				emptyMessage={CAMPAIGN_COPY.notConfigured}
			>
				{null}
			</ReportingChartFrame>
		)
	}

	const campaigns = data?.configured ? data.campaigns : []
	const standard = campaigns.filter((campaign) => campaign.shape === "standard")
	const custom = campaigns.filter((campaign) => campaign.shape === "custom")
	const funnels = data?.configured ? [data.total, ...standard] : []
	const funnel = funnels.find((item) => item.id === selected) ?? funnels[0]

	const controls = (
		<div className="flex items-center gap-2">
			{data?.configured ? (
				<span className="text-xs text-tertiary">
					Updated{" "}
					{formatDistanceToNowStrict(new Date(data.fetchedAt), {
						addSuffix: true,
					})}
				</span>
			) : null}
			<Tooltip label="Reload from lemlist">
				<IconButton
					variant="ghost"
					size="sm"
					Icon={IconArrowRotateClockwiseOutline18}
					disabled={isRefreshing}
					onClick={() => void refresh()}
					aria-label="Reload from lemlist"
				/>
			</Tooltip>
			<Tabs
				value={status}
				onValueChange={(value) => setStatus(value as Status)}
			>
				<TabsList variant="transparent">
					<TabsTrigger variant="transparent" value="running">
						Running
					</TabsTrigger>
					<TabsTrigger variant="transparent" value="all">
						All
					</TabsTrigger>
				</TabsList>
			</Tabs>
		</div>
	)

	return (
		<div className="flex flex-col gap-4">
			<ReportingChartFrame
				title="Campaigns"
				description={CAMPAIGN_COPY.campaigns}
				legend={controls}
				isLoading={query.isLoading}
				isEmpty={!query.isLoading && campaigns.length === 0}
				emptyMessage={
					query.isError
						? `Couldn't load campaigns: ${query.error.message}`
						: "No campaigns here"
				}
			>
				{data?.configured ? (
					<ComparisonTable
						campaigns={campaigns}
						total={data.total}
						showStatus={status === "all"}
					/>
				) : null}
			</ReportingChartFrame>

			{funnel ? (
				<ReportingChartFrame
					title="Funnel"
					description={CAMPAIGN_COPY.funnel}
					legend={
						<Tabs value={funnel.id} onValueChange={setSelected}>
							<TabsList variant="transparent">
								{funnels.map((item) => (
									<TabsTrigger
										key={item.id}
										variant="transparent"
										value={item.id}
									>
										{campaignShortName(item.name)}
									</TabsTrigger>
								))}
							</TabsList>
						</Tabs>
					}
				>
					<FunnelView funnel={funnel} />
				</ReportingChartFrame>
			) : null}

			{standard.length > 0 ? (
				<ReportingChartFrame title="By step" description={CAMPAIGN_COPY.byStep}>
					<StepTable campaigns={standard} />
				</ReportingChartFrame>
			) : null}

			{custom.map((campaign) => (
				<ReportingChartFrame
					key={campaign.id}
					title={campaignShortName(campaign.name)}
					description={CAMPAIGN_COPY.customShape}
				>
					<RawSteps campaign={campaign} />
				</ReportingChartFrame>
			))}
		</div>
	)
}

function ComparisonTable({
	campaigns,
	total,
	showStatus,
}: {
	campaigns: CampaignFunnel[]
	total: CampaignFunnel
	showStatus: boolean
}) {
	const rows = campaigns.length > 1 ? [...campaigns, total] : campaigns
	return (
		<div className="overflow-x-auto">
			<table className="w-full min-w-[900px] text-sm">
				<thead>
					<tr className="border-b border-gray-200 text-xs text-tertiary dark:border-gray-700/80">
						<th className="px-2 pb-2 text-left font-normal">Campaign</th>
						<th className="px-2 pb-2 text-right font-normal">Leads</th>
						<th className="px-2 pb-2 text-right font-normal">Invited</th>
						<th className="px-2 pb-2 text-right font-normal">Accepted</th>
						<th className="px-2 pb-2 text-right font-normal">LinkedIn lane</th>
						<th className="px-2 pb-2 text-right font-normal">Email lane</th>
						<th className="px-2 pb-2 text-right font-normal">Replied</th>
						<th className="px-2 pb-2 text-right font-normal">Waiting</th>
						<th className="px-2 pb-2 text-right font-normal">Bounced</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => {
						const isTotal = row.id === total.id
						return (
							<tr
								key={row.id}
								className={cn(borderRow, isTotal && "font-medium")}
							>
								<td className="max-w-[280px] px-2 py-2">
									<div className="flex items-center gap-2">
										<Tooltip label={row.name}>
											<span className="truncate text-primary">
												{campaignShortName(row.name)}
											</span>
										</Tooltip>
										{showStatus && !isTotal ? (
											<Badge size="sm">{row.status}</Badge>
										) : null}
									</div>
								</td>
								<NumberCell value={row.leads} />
								<NumberCell value={row.inviteSent} of={row.leads} />
								<NumberCell value={row.accepted} of={row.inviteSent} />
								<NumberCell value={row.lanes?.linkedin.leads} />
								<NumberCell value={row.lanes?.email.leads} />
								<NumberCell value={row.replied} of={row.reached} />
								<NumberCell value={waitingTotal(row)} />
								<NumberCell value={row.bounced} />
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

function NumberCell({ value, of }: { value: number | undefined; of?: number }) {
	return (
		<td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
			<span className={value ? "text-primary" : "text-tertiary"}>
				{value ?? "-"}
			</span>
			{of !== undefined ? (
				<span className="ml-1.5 inline-block w-12 text-tertiary">
					{percent(value ?? 0, of)}%
				</span>
			) : null}
		</td>
	)
}

function FunnelView({ funnel }: { funnel: CampaignFunnel }) {
	const top = [
		{ label: "Leads", value: funnel.leads },
		{ label: "Invite sent", value: funnel.inviteSent },
		{ label: "Accepted", value: funnel.accepted },
	]
	return (
		<div className="flex flex-col gap-6 text-sm">
			<div className="flex flex-col gap-0.5">
				{top.map((row) => (
					<Bar
						key={row.label}
						label={row.label}
						value={row.value}
						base={funnel.leads}
						note={`${percent(row.value, funnel.leads)}%`}
					/>
				))}
				<p className="mt-2 px-2 text-xs text-tertiary">
					{laneFootnote(
						funnel.lanes?.linkedin.leads ?? 0,
						funnel.lanes?.email.leads ?? 0,
						funnel.notBranched,
					)}
				</p>
			</div>
			{funnel.lanes ? (
				<div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
					<LaneView lane="linkedin" data={funnel.lanes.linkedin} />
					<LaneView lane="email" data={funnel.lanes.email} />
				</div>
			) : null}
		</div>
	)
}

function LaneView({ lane, data }: { lane: Lane; data: LaneFunnel }) {
	return (
		<div className="flex flex-col gap-0.5">
			<div className="mb-1 flex items-baseline justify-between px-2">
				<span className="text-xs font-medium text-secondary">
					{LANE_LABEL[lane]}
				</span>
				<span className="text-xs tabular-nums text-tertiary">
					{data.leads} leads, {data.replied} replied
				</span>
			</div>
			{data.steps.map((step) => (
				<Bar
					key={step.key}
					label={step.label}
					value={step.sent}
					replied={step.replied}
					base={data.leads}
					note={step.waiting > 0 ? `${step.waiting} waiting` : ""}
				/>
			))}
		</div>
	)
}

function Bar({
	label,
	value,
	replied,
	base,
	note,
}: {
	label: string
	value: number
	replied?: number
	base: number
	note: string
}) {
	const width = percent(value, base)
	const replyWidth = replied ? percent(replied, base) : 0
	return (
		<div className="grid grid-cols-[8.5rem_minmax(0,1fr)_3.5rem_4.5rem_5.5rem] items-center gap-3 px-2 py-1.5">
			<span className="truncate text-secondary">{label}</span>
			<Tooltip
				label={
					replied !== undefined
						? `${value} sent, ${replied} replied (${percent(replied, value)}%)`
						: `${value} of ${base}`
				}
				shouldWrapChildren
			>
				<span className="relative block h-5 w-full">
					<span
						className="absolute inset-y-0 left-0 rounded-r"
						style={{
							width: `${Math.max(width, value > 0 ? 1 : 0)}%`,
							backgroundColor: toneColor("primary"),
						}}
					/>
					{replyWidth > 0 ? (
						<span
							className="absolute inset-y-0 left-0 rounded-r"
							style={{
								width: `${Math.max(replyWidth, 1)}%`,
								backgroundColor: toneColor("secondary"),
							}}
						/>
					) : null}
				</span>
			</Tooltip>
			<span className="text-right tabular-nums text-primary">{value}</span>
			<span className="text-right tabular-nums text-tertiary">
				{replied !== undefined ? `${replied} replied` : ""}
			</span>
			<span className="text-right tabular-nums text-tertiary">{note}</span>
		</div>
	)
}

function StepTable({ campaigns }: { campaigns: CampaignFunnel[] }) {
	const lanes: Lane[] = ["linkedin", "email"]
	return (
		<div className="overflow-x-auto">
			<table className="w-full min-w-[720px] text-sm">
				<thead>
					<tr className="border-b border-gray-200 text-xs text-tertiary dark:border-gray-700/80">
						<th className="px-2 pb-2 text-left font-normal">Step</th>
						{campaigns.map((campaign) => (
							<th
								key={campaign.id}
								className="px-2 pb-2 text-right font-normal"
							>
								{campaignShortName(campaign.name)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{lanes.map((lane) => (
						<LaneRows key={lane} lane={lane} campaigns={campaigns} />
					))}
				</tbody>
			</table>
		</div>
	)
}

function LaneRows({
	lane,
	campaigns,
}: {
	lane: Lane
	campaigns: CampaignFunnel[]
}) {
	return (
		<>
			<tr className={borderRow}>
				<td
					colSpan={campaigns.length + 1}
					className="px-2 pb-1 pt-3 text-xs font-medium text-secondary"
				>
					{LANE_LABEL[lane]}
				</td>
			</tr>
			{stepRows(campaigns, lane).map((row) => (
				<tr key={row.key} className={borderRow}>
					<td className="whitespace-nowrap px-2 py-2 text-secondary">
						{row.label}
					</td>
					{row.cells.map((cell, index) => (
						<td
							key={campaigns[index]?.id ?? index}
							className="whitespace-nowrap px-2 py-2 text-right tabular-nums"
						>
							{cell ? (
								<>
									<span className="text-primary">{cell.sent}</span>
									<span className="text-tertiary">
										{" "}
										sent · {cell.replied} replied ·{" "}
										{percent(cell.replied, cell.sent)}%
									</span>
								</>
							) : (
								<span className="text-tertiary">-</span>
							)}
						</td>
					))}
				</tr>
			))}
		</>
	)
}

function RawSteps({ campaign }: { campaign: CampaignFunnel }) {
	return (
		<table className="w-full text-sm">
			<tbody>
				{campaign.steps.map((step, index) => (
					<tr key={step.stepId} className={borderRow}>
						<td className="px-2 py-2 text-secondary">
							{index + 1}. {step.type}
						</td>
						<td className="px-2 py-2 text-right tabular-nums">
							<span className="text-primary">{step.sent}</span>
							<span className="text-tertiary">
								{" "}
								sent · {step.replied} replied
							</span>
						</td>
					</tr>
				))}
			</tbody>
		</table>
	)
}
